import { Validators } from '@biteinc/common';
import { allEnumValues, AutoAddScannableItemsMode, AutoAddScannableItemsModeHelper, KioskOrderingMode, KioskOrderingModeHelper, KioskReducedHeightSize, KioskReducedHeightSizeHelper, LanguageCode, LeadTimeSource, LeadTimeSourceHelper, LoyaltyInMenuMode, LoyaltyInMenuModeHelper, LoyaltyInPaymentOptionsMode, LoyaltyInPaymentOptionsModeHelper, LoyaltyPostAuthStep, LoyaltySignupMethod, LoyaltySignupMode, LoyaltySignupModeHelper, ManagerCodeForScannedItemsMode, ManagerCodeForScannedItemsModeHelper, MenuItemCategory, MenuItemViewDetailsStyle, MenuItemViewDetailsStyleHelper, MenuNavigationViewType, MenuNavigationViewTypeHelper, MenuSectionHeaderType, MenuSectionHeaderTypeHelper, OrderUpdateToastsMode, OrderUpdateToastsModeHelper, SecretMenuType, SideNavImageSize, SimpleLoyaltySignupMode, UserRight, } from '@biteinc/enums';
export const kioskSettingsSchema = {
    // Location Model Settings
    // KIOSK SETTINGS
    kioskOrderingMode: {
        displayName: 'Ordering Mode',
        type: 'stringEnum',
        enum: KioskOrderingMode,
        enumNameHelper: KioskOrderingModeHelper.name,
        required: true,
        tabId: 'kiosk-settings-v2',
        biteOnly: true,
        ui: 'dropdown',
    },
    autoAddScannableItemsMode: {
        type: 'stringEnum',
        enum: AutoAddScannableItemsMode,
        enumNameHelper: AutoAddScannableItemsModeHelper.name,
        displayName: 'Auto-add scannable items to the menu',
        required: true,
        tabId: 'kiosk-settings-v2',
        biteOnly: true,
        ui: 'dropdown',
        tooltip: {
            text: 'When set, unarchived menu items with barcodes that are not in any visible section will ' +
                'automatically be available for barcode scanning at the kiosk, without needing to be ' +
                'placed in a hidden section.',
        },
    },
    leadTimeSource: {
        type: 'stringEnum',
        enum: LeadTimeSource,
        enumNameHelper: LeadTimeSourceHelper.name,
        displayName: 'Order lead time source',
        required: true,
        tabId: 'kiosk-settings-v2',
        biteOnly: true,
        ui: 'dropdown',
        tooltip: {
            text: 'Where the estimated order lead time comes from. Location Lead Time Rules uses the ' +
                'section-based item count rules configured on the location. Menu Item Prep Times uses ' +
                'the longest prep time among the items and mods in the cart. Menu Item Prep Times only ' +
                'applies to kiosk (vitrine) orders; third-party orders and GCN in-cart warnings always ' +
                'use the location rules.',
        },
    },
    ignoreNonNumericalBarcodes: {
        type: 'bool',
        displayName: 'Ignore non-numerical barcodes in scanner mode',
        required: true,
        tabId: 'kiosk-settings-v2',
        biteOnly: true,
        tooltip: {
            text: 'When enabled, barcodes containing non-numeric characters will be ignored. UPC codes are always numeric.',
        },
    },
    clerkAttentionSoundRepeatIntervalMs: {
        type: 'int',
        displayName: 'Clerk attention sound repeat interval (ms)',
        required: true,
        tabId: 'kiosk-settings-v2',
        biteOnly: true,
        tooltip: {
            text: 'How often to repeat the clerk attention sound when verification screens are shown (e.g., alcohol verification, void item). Set to 0 to disable repeating.',
        },
    },
    korAlertStabilizationWindowMinutes: {
        type: 'int',
        displayName: 'KOR alert stabilization window (minutes)',
        required: true,
        tabId: 'kiosk-settings-v2',
        biteOnly: true,
        tooltip: {
            text: 'How long a kiosk must remain stable before KOR sends a resolved alert.',
        },
    },
    orderUpdateToasts: {
        type: 'stringEnum',
        enum: OrderUpdateToastsMode,
        enumNameHelper: OrderUpdateToastsModeHelper.name,
        displayName: 'Order update toasts',
        required: true,
        tabId: 'kiosk-settings-v2',
        biteOnly: true,
        ui: 'dropdown',
        tooltip: {
            text: 'When to show toast notifications for order updates. "All Updates" shows toasts for items added and discounts. "Scanned Items and Discounts" shows toasts only for items added by scanning a barcode, plus discounts. "Discounts Only" shows toasts only for discounts applied. Scanner-only kiosks always show all toasts.',
        },
    },
    loyaltySignup: {
        type: 'object',
        displayName: 'Loyalty Signup',
        required: true,
        isNullable: true,
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        fields: {
            mode: {
                type: 'stringEnum',
                displayName: 'Mode',
                enum: LoyaltySignupMode,
                enumNameHelper: LoyaltySignupModeHelper.name,
                required: true,
                ui: 'dropdown',
                tooltip: {
                    text: 'Simple: Allow customers to have a SMS message with loyalty sign up details sent to them. Requires a Twilio integration. | Instant Enroll: Allow customers to sign up for loyalty from the kiosk. Requires Vitrine. Requires Punchh integration.',
                    isWarning: true,
                },
                writeRight: UserRight.ManageChannelSettings,
            },
            simpleSmsTemplate: {
                displayName: 'SMS Message',
                type: 'keyString',
                validationRegex: Validators.smsRegexString,
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.Simple],
                    },
                ],
                tooltip: {
                    text: 'Message to send when a customer enters their number, should include sign up URL.',
                },
                isNullable: true,
                required: true,
                defaultValue: null,
                writeRight: UserRight.ManageChannelSettings,
            },
            simpleMode: {
                type: 'int',
                required: true,
                displayName: 'Signup Mode',
                values: allEnumValues(SimpleLoyaltySignupMode),
                defaultValue: SimpleLoyaltySignupMode.OrderSummary,
                ui: 'dropdown',
                tooltip: {
                    text: 'Control where in the flow the user may request the loyalty sign up SMS message. To turn off loyalty sign up, clear the Loyalty Signup setting.',
                },
                writeRight: UserRight.ManageChannelSettings,
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.Simple],
                    },
                ],
            },
            loyaltySignupMethods: {
                displayName: 'Loyalty Signup Methods',
                placeholder: 'Add a new method',
                tooltip: {
                    text: 'The methods that are allowed for customers to sign up for loyalty. Prompts will appear in the order they are listed here.',
                },
                defaultValue: [],
                exclusiveTo: 'vitrine',
                type: 'array',
                elementType: 'stringEnum',
                elementEnum: LoyaltySignupMethod,
                ui: 'table',
                required: true,
                writeRight: UserRight.ManageChannelSettings,
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.Simple],
                    },
                ],
            },
            simpleBannerImage: {
                type: 'array',
                elementType: 'object',
                elementSubtype: 'Image',
                elementUniqueIdFieldName: 'origUrl',
                maxCount: 1,
                displayName: 'Signup Banner Image',
                keepAspectRatio: 'contain',
                size: {
                    width: 680,
                    height: 180,
                },
                defaultValue: [],
                tooltip: {
                    text: 'The image to display on the banner on the prompting the user to signup for loyalty. If absent, the phrase "Signup for Loyalty" will be displayed.',
                },
                writeRight: UserRight.ManageChannelSettings,
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.Simple],
                    },
                    {
                        field: 'simpleMode',
                        values: [SimpleLoyaltySignupMode.PaymentCompleted],
                    },
                ],
            },
            qrCodeImage: {
                type: 'array',
                elementType: 'object',
                elementSubtype: 'Image',
                elementUniqueIdFieldName: 'origUrl',
                maxCount: 1,
                displayName: 'Sign Up QR Code Image',
                size: {
                    width: 600,
                    height: 600,
                },
                defaultValue: [],
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.Simple],
                    },
                ],
            },
            postAuthSteps: {
                displayName: 'Post Auth Steps',
                type: 'array',
                elementType: 'stringEnum',
                elementEnum: LoyaltyPostAuthStep,
                ui: 'table',
                required: true,
                minCount: 1,
                defaultValue: [LoyaltyPostAuthStep.Rewards],
                placeholder: 'Add a new step',
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.InstantEnroll],
                    },
                ],
            },
            nameRequired: {
                displayName: 'Name Required',
                type: 'bool',
                tooltip: {
                    text: 'Whether the guest name field is required during instant enroll signup. When disabled, name is still shown but optional.',
                },
                writeRight: UserRight.ManageChannelSettings,
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.InstantEnroll],
                    },
                ],
            },
            emailRequired: {
                displayName: 'Email Required',
                type: 'bool',
                tooltip: {
                    text: 'When enabled, the kiosk requires a valid email address during instant enroll signup. Punchh and Punchh Olo send the email address when they create the loyalty account.',
                },
                writeRight: UserRight.ManageChannelSettings,
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.InstantEnroll],
                    },
                ],
            },
            customFields: {
                displayName: 'Custom Fields',
                type: 'array',
                elementType: 'object',
                ui: 'accordion',
                uiSubtype: 'LoyaltySignupCustomFields',
                fields: {
                    key: {
                        type: 'keyString',
                        required: true,
                    },
                    value: {
                        type: 'keyString',
                        required: true,
                    },
                },
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.InstantEnroll],
                    },
                ],
            },
            rewardsApiKey: {
                type: 'keyString',
                displayName: 'Rewards API Key',
                maskInput: true,
                tooltip: {
                    text: 'API key used to gift rewards upon loyalty signup. May not be required based on loyalty provider.',
                },
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.InstantEnroll],
                    },
                ],
            },
            rewardIds: {
                type: 'array',
                elementType: 'keyString',
                displayName: 'Reward IDs',
                uniqueElements: true,
                tooltip: {
                    text: 'Enter a list of reward IDs to be gifted upon loyalty signup. May require "Rewards API Key" to be set based on loyalty provider.',
                },
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.InstantEnroll],
                    },
                ],
            },
            // Not all orgs will want to send an opt-in SMS from loyalty.
            // For example, Portillo's already sends one from their marketing platform.
            sendLoyaltyOptInSms: {
                type: 'bool',
                displayName: 'Send Loyalty Opt-In SMS',
                tooltip: {
                    text: 'If true, tells the loyalty provider to send an opt-in SMS to the guest if they create a loyalty account.',
                },
                conditions: [
                    {
                        field: 'mode',
                        values: [LoyaltySignupMode.InstantEnroll],
                    },
                ],
            },
        },
    },
    requireManagerCodeForAlcohol: {
        displayName: 'Require manager code for alcohol purchases',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        tooltip: {
            text: 'If the guest has an alcoholic item in their cart, checkout will be guarded by a manager code.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    // Superseded by managerCodeForScannedItemsMode, which the settings controller derives this from.
    // Still what kiosk builds before the mode release read, so it has to stay accurate. Remove once
    // no build in the field reads it.
    requireManagerCodeForRemovingScannedItems: {
        displayName: 'Require manager code for scanned items (deprecated)',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        isHidden: true,
        isReadOnly: true,
        tooltip: {
            text: 'In scanner-only mode, an employee access code is required to remove a scanned item from the cart, or to exit with scanned items in the cart.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    managerCodeForScannedItemsMode: {
        displayName: 'Require manager code for scanned items',
        type: 'stringEnum',
        enum: ManagerCodeForScannedItemsMode,
        enumNameHelper: ManagerCodeForScannedItemsModeHelper.name,
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        ui: 'dropdown',
        tooltip: {
            text: 'In scanner-only mode, choose when an employee access code is required. ' +
                '"On scanned item removal only" asks for the code when a guest removes a scanned item ' +
                'from the cart. "On scanned item removal and menu exit" also asks when a guest exits the ' +
                'menu with scanned items still in the cart. "Never" lets guests remove scanned items and ' +
                'exit the menu on their own.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    checkoutCompleteTimeout: {
        type: 'int',
        required: true,
        displayName: 'Timeout after checkout in ms',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Default: 20000. If set to zero, uses default.',
        },
        min: 0,
    },
    menuTimeout: {
        type: 'int',
        required: true,
        displayName: 'Timeout after inactivity in menu in ms',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Default: 120000. If set to zero, uses default.',
        },
    },
    promptBeforeTimeout: {
        type: 'bool',
        required: true,
        displayName: 'Prompt Before Closing Menu on Inactivity',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        exclusiveTo: 'gcn',
    },
    promptTimeout: {
        type: 'int',
        required: true,
        displayName: 'Prompt Timeout in ms',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Default: 15000. If set to zero, uses default.',
        },
        exclusiveTo: 'vitrine',
    },
    adaZoomEnabled: {
        type: 'bool',
        required: true,
        displayName: 'Enable ADA Zoom Option',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    allowAccessibleMenu: {
        type: 'bool',
        required: true,
        displayName: 'Enable ADA Reduced Height Option',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Accessibility feature for allowing guests to reduce the menu height and restrict it to half the kiosk screen.',
            isWarning: true,
        },
    },
    showStormButtonInBottomBar: {
        type: 'bool',
        required: true,
        displayName: 'Show Storm Button In Bottom Bar',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        exclusiveTo: 'vitrine',
    },
    supportedLanguages: {
        type: 'array',
        elementType: 'stringEnum',
        elementEnum: LanguageCode,
        required: true,
        minCount: 1,
        ui: 'table',
        displayName: 'Supported Languages',
        placeholder: 'Add a new language (first is default)',
        writeRight: UserRight.ManageSupportedLanguages,
        tabId: 'kiosk-settings-v2',
    },
    maintenanceLanguages: {
        type: 'array',
        elementType: 'stringEnum',
        elementEnum: LanguageCode,
        required: true,
        minCount: 1,
        ui: 'table',
        displayName: 'Maintenance Languages',
        placeholder: 'Add a new language (first is default)',
        tooltip: {
            text: 'The languages to display the maintenance cover in. Only the first two languages will be displayed.',
        },
        writeRight: UserRight.ManageSupportedLanguages,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
    },
    showPageNavigation: {
        type: 'bool',
        required: true,
        displayName: 'Show Page Navigation (GCN)',
        tabId: 'kiosk-settings-v2',
        biteOnly: true,
        exclusiveTo: 'gcn',
        tooltip: {
            text: 'Show a page navigation screen upon entering the menu. GCN only — on Vitrine the screen is controlled by Page Navigation Settings, which shows it whenever it is configured.',
        },
    },
    pageNavigationSettings: {
        type: 'object',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        displayName: 'Page Navigation Settings',
        // No condition on showPageNavigation: this object is Vitrine's own on/off switch. Clearing it
        // hides the page-navigation screen; configuring it shows one. A condition would also drop the
        // field from any write made while the GCN bool was off (schema_inflator), which is exactly the
        // state Vitrine now needs to be able to leave.
        isNullable: true,
        fields: {
            columnCount: {
                type: 'int',
                displayName: 'Column Count',
                biteOnly: true,
                min: 2,
                max: 3,
                ui: 'radiolist',
                required: true,
            },
            displayType: {
                type: 'stringEnum',
                displayName: 'Page Navigation Text Display Type',
                enum: MenuSectionHeaderType,
                enumNameHelper: MenuSectionHeaderTypeHelper.name,
                required: true,
                biteOnly: true,
            },
            size: {
                type: 'keyString',
                displayName: 'Page Navigation Banner Dimensions',
                biteOnly: true,
                ui: 'radiolist',
                required: true,
            },
            bannerImageHeight: {
                type: 'int',
                displayName: 'Page Navigation Banner Image Pixel Height',
                isNullable: true,
            },
            backgroundImage: {
                type: 'array',
                elementType: 'object',
                elementSubtype: 'Image',
                elementUniqueIdFieldName: 'origUrl',
                maxCount: 1,
                displayName: 'Page Navigation Background Image',
                biteOnly: true,
                maxFileSizeMb: 20,
                size: {
                    width: 2048,
                    height: 2732,
                },
            },
            useOrderHistorySectionBanner: {
                type: 'bool',
                displayName: 'Enable Order History Section Banner',
                biteOnly: true,
                tooltip: {
                    text: 'If enabled, the order history section will be displayed as a banner on the page navigation screen.',
                },
            },
        },
    },
    closedCoverImage: {
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        displayName: 'Closed Kiosk Cover Image - iOS',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageMenuCovers,
        maxFileSizeMb: 20,
        size: {
            width: 2048,
            height: 2732,
        },
    },
    closedCoverImage1080: {
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        displayName: 'Closed Kiosk Cover Image - 1080p',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageMenuCovers,
        maxFileSizeMb: 20,
        size: {
            width: 1080,
            height: 1920,
        },
    },
    loyaltyScanQRCodeImage: {
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        canHaveLocalizationOverrides: true,
        maxCount: 1,
        displayName: 'Loyalty Scan QR Code Image',
        tooltip: {
            text: 'Image to show on the opening loyalty login screen to prompt the user to scan their QR code.',
        },
        tabId: 'kiosk-settings-v2',
        size: {
            width: 600,
            height: 600,
        },
    },
    askToIncludeUtensilsOnKioskOrders: {
        type: 'bool',
        required: true,
        displayName: 'Utensils opt in on Kiosk orders',
        tabId: 'kiosk-settings-v2',
        tooltip: {
            text: 'If set it will send "include Utensils" to the POS',
        },
    },
    skipPhoneNumberIfProvidedByLoyalty: {
        type: 'bool',
        required: true,
        displayName: 'Skip phone number if provided by loyalty',
        writeRight: UserRight.ManageChannelSettings,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'gcn',
    },
    menuNavigation: {
        displayName: 'Menu Navigation',
        required: true,
        type: 'object',
        tabId: 'kiosk-settings-v2',
        fields: {
            type: {
                type: 'stringEnum',
                enum: MenuNavigationViewType,
                enumNameHelper: MenuNavigationViewTypeHelper.name,
                required: true,
                ui: 'dropdown',
            },
            sideNav: {
                displayName: 'Side Nav Configuration',
                required: true,
                isNullable: true,
                type: 'object',
                conditions: [
                    {
                        field: 'type',
                        values: [MenuNavigationViewType.Side, MenuNavigationViewType.TopAndSide],
                    },
                ],
                fields: {
                    collapsed: {
                        type: 'bool',
                        defaultValue: false,
                        tooltip: {
                            text: 'If enabled, the side nav will collapse sub-items by default.',
                        },
                        exclusiveTo: 'vitrine',
                    },
                    showText: {
                        type: 'bool',
                        defaultValue: true,
                        tooltip: {
                            text: 'If enabled, the side nav will show text.',
                        },
                        exclusiveTo: 'vitrine',
                    },
                    imageSize: {
                        displayName: 'Image Size',
                        type: 'stringEnum',
                        enum: SideNavImageSize,
                        required: true,
                        ui: 'dropdown',
                        exclusiveTo: 'vitrine',
                    },
                    showSubSectionImages: {
                        displayName: 'Show Sub Section Images',
                        type: 'bool',
                        required: true,
                        defaultValue: false,
                        tooltip: {
                            text: 'If enabled, the side nav will show images for each sub section.',
                        },
                        exclusiveTo: 'vitrine',
                    },
                    showScrollIndicator: {
                        displayName: 'Show Scroll Indicator',
                        type: 'bool',
                        required: true,
                        defaultValue: false,
                        tooltip: {
                            text: 'If enabled, the side nav will show a scroll indicator icon at the bottom.',
                        },
                        exclusiveTo: 'vitrine',
                    },
                    alwaysShowSubNav: {
                        displayName: 'Always Show Sub Nav',
                        type: 'bool',
                        tooltip: {
                            text: 'If enabled, the side nav will always show sub-sections and sub-pages, even when there is only one.',
                        },
                        exclusiveTo: 'vitrine',
                    },
                },
            },
        },
    },
    sideNav: {
        displayName: 'Side Nav Configuration',
        isHidden: true,
        isNullable: true,
        required: true,
        type: 'object',
        tabId: 'kiosk-settings-v2',
        fields: {
            collapsed: {
                type: 'bool',
                defaultValue: false,
                tooltip: {
                    text: 'If enabled, the side nav will collapse sub-items by default.',
                },
                exclusiveTo: 'vitrine',
            },
            showText: {
                type: 'bool',
                defaultValue: true,
                tooltip: {
                    text: 'If enabled, the side nav will show text.',
                },
                exclusiveTo: 'vitrine',
            },
            imageSize: {
                displayName: 'Image Size',
                type: 'stringEnum',
                enum: SideNavImageSize,
                required: true,
                ui: 'dropdown',
                exclusiveTo: 'vitrine',
            },
            showSubSectionImages: {
                displayName: 'Show Sub Section Images',
                type: 'bool',
                required: true,
                defaultValue: false,
                tooltip: {
                    text: 'If enabled, the side nav will show images for each sub section.',
                },
                exclusiveTo: 'vitrine',
            },
            showScrollIndicator: {
                displayName: 'Show Scroll Indicator',
                type: 'bool',
                required: true,
                defaultValue: false,
                tooltip: {
                    text: 'If enabled, the side nav will show a scroll indicator icon at the bottom.',
                },
                exclusiveTo: 'vitrine',
            },
            alwaysShowSubNav: {
                displayName: 'Always Show Sub Nav',
                type: 'bool',
                tooltip: {
                    text: 'If enabled, the side nav will always show sub-sections and sub-pages, even when there is only one.',
                },
                exclusiveTo: 'vitrine',
            },
        },
    },
    areKioskDownAlertsCritical: {
        type: 'bool',
        required: true,
        displayName: '"Kiosk Down" Alerts are Critical',
        tabId: 'kiosk-settings-v2',
        defaultValue: true,
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'If disabled, any "Kiosk Down" alerts configured will not be sent while the location is closed. Non-live locations will always send any configured "Kiosk Down" alerts.',
        },
    },
    loyaltyInMenuMode: {
        displayName: 'How to Display Loyalty in the Menu',
        type: 'stringEnum',
        enum: LoyaltyInMenuMode,
        enumNameHelper: LoyaltyInMenuModeHelper.name,
        required: true,
        ui: 'dropdown',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
    },
    loyaltyPostAuthSteps: {
        displayName: 'Loyalty Post Auth Steps',
        type: 'array',
        elementType: 'stringEnum',
        elementEnum: LoyaltyPostAuthStep,
        tabId: 'kiosk-settings-v2',
        ui: 'table',
        required: true,
        minCount: 1,
        exclusiveTo: 'vitrine',
        writeRight: UserRight.ManageChannelSettings,
        placeholder: 'Add a new step',
    },
    useLoyaltySignupV2InCheckout: {
        displayName: 'Use Loyalty Auth V2 in Checkout',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'gcn',
        writeRight: UserRight.ManageChannelSettings,
    },
    loyaltyInPaymentOptionsMode: {
        displayName: 'How to Display Loyalty in Payment Options',
        type: 'stringEnum',
        enum: LoyaltyInPaymentOptionsMode,
        enumNameHelper: LoyaltyInPaymentOptionsModeHelper.name,
        required: true,
        ui: 'dropdown',
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        writeRight: UserRight.ManageChannelSettings,
    },
    loyaltyAccountScrimImage: {
        displayName: 'Loyalty Account Scrim Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'gcn',
        size: {
            width: 500,
            height: 150,
        },
        conditions: [
            {
                field: 'loyaltyInMenuMode',
                values: allEnumValues(LoyaltyInMenuMode).filter((loyaltyInMenuMode) => {
                    return loyaltyInMenuMode !== LoyaltyInMenuMode.Off;
                }),
            },
        ],
    },
    loyaltyAccountScrimSignUpImage: {
        displayName: 'Loyalty Account Scrim Sign Up Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        canHaveLocalizationOverrides: true,
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'gcn',
        size: {
            width: 500,
            height: 150,
        },
        conditions: [
            {
                field: 'loyaltyInMenuMode',
                values: allEnumValues(LoyaltyInMenuMode).filter((loyaltyInMenuMode) => {
                    return loyaltyInMenuMode !== LoyaltyInMenuMode.Off;
                }),
            },
        ],
    },
    vitrineLoyaltyScrimImage: {
        displayName: 'Vitrine Loyalty Scrim Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        canHaveLocalizationOverrides: true,
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 900,
            height: 300,
        },
    },
    vitrineLoyaltyCheckoutScrimImage: {
        displayName: 'Vitrine Loyalty Instant Enroll at Checkout Scrim Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        canHaveLocalizationOverrides: true,
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 900,
            height: 300,
        },
    },
    donationImage: {
        displayName: 'Donation Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        canHaveLocalizationOverrides: true,
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 900,
            height: 300,
        },
    },
    phoneNumberScrimImage: {
        displayName: 'Phone Number Collection Modal Scrim Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        canHaveLocalizationOverrides: true,
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        size: {
            width: 750,
            height: 300,
        },
    },
    secretMenu: {
        displayName: 'Secret Menu',
        isNullable: true,
        required: true,
        type: 'object',
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        fields: {
            type: {
                type: 'stringEnum',
                enum: SecretMenuType,
                required: true,
                ui: 'dropdown',
            },
            sideNavImage: {
                displayName: 'Side Nav Image',
                type: 'array',
                elementType: 'object',
                elementSubtype: 'Image',
                elementUniqueIdFieldName: 'origUrl',
                canHaveLocalizationOverrides: true,
                required: true,
                maxCount: 1,
                size: {
                    width: 352,
                    height: 198,
                },
                conditions: [
                    {
                        field: 'type',
                        values: allEnumValues(SecretMenuType).filter((secretMenuType) => {
                            return secretMenuType === SecretMenuType.SideNav;
                        }),
                    },
                ],
            },
            requiresLoyalty: {
                displayName: 'Require Loyalty for Secret Menu',
                type: 'bool',
                defaultValue: false,
                tooltip: {
                    text: 'If enabled, guests must be logged in to loyalty to access the secret menu.',
                },
            },
            columnCount: {
                type: 'int',
                displayName: 'Column Count',
                biteOnly: true,
                min: 2,
                max: 3,
                defaultValue: 3,
                ui: 'radiolist',
                required: true,
            },
            accessCodes: {
                type: 'array',
                elementType: 'keyString',
                displayName: 'Access Codes',
                biteOnly: true,
                required: true,
                isNullable: true,
                uniqueElements: true,
                tooltip: {
                    text: 'Enter a list of access codes that will allow guests to access the secret menu. If left blank, the secret menu will be accessible to all guests.',
                },
            },
        },
    },
    showMaintenanceScreenIfPrinterIsNotWorking: {
        type: 'bool',
        required: true,
        displayName: 'Show Maintenance Screen If Printer Is Not Working',
        tabId: 'kiosk-settings-v2',
        tooltip: {
            text: 'Printer could be disconnected, out of paper, or with open cover. Even if this setting is off, we may still have this behavior at locations where printing is critical (e.g. QR code)',
            isWarning: true,
        },
    },
    showMaintenanceScreenIfTableTrackerEmpty: {
        displayName: 'Show Maintenance Screen if Table Tracker is Empty',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, the maintenance screen will be shown if the table tracker is empty.',
        },
    },
    showMaintenanceScreenIfScannerIsNotWorking: {
        displayName: 'Show Maintenance Screen if Scanner is Not Working',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, the maintenance screen will be shown if the scanner is not working.',
        },
    },
    enableComboUpsellAtCheckout: {
        type: 'bool',
        required: true,
        displayName: 'Enable combo upsell at checkout',
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'Enable to show the "Make it a Combo" button on the item details in cart',
        },
    },
    hidePricesUntilValidation: {
        type: 'bool',
        required: true,
        displayName: 'Hide prices until validation',
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'When enabled, hides prices on the cart screen and suppresses price change messaging (strikethrough prices, subtotal change warning) until the order is validated.',
        },
    },
    restartKiosksTime: {
        displayName: 'Restart kiosks time',
        type: 'dayTimestamp',
        biteOnly: true,
        required: true,
        isNullable: true,
        tabId: 'kiosk-settings-v2',
    },
    detectionPrefixGiftCard: {
        type: 'keyString',
        displayName: 'Gift Card Detection Prefix',
        tooltip: {
            text: 'Prefix for gift card numbers to detect while in payment options screen. This allows users to immediately scan a gift card without having to tap the "Gift Card" button.',
        },
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        biteOnly: true,
        isNullable: true,
    },
    detectionPrefixLoyalty: {
        type: 'keyString',
        displayName: 'Loyalty Detection Prefix',
        tooltip: {
            text: 'Prefix for loyalty barcodes to detect on the cover, menu, and cart screens. A scan that starts with this prefix routes the guest directly into the loyalty login flow with the scanned value.',
        },
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        biteOnly: true,
        isNullable: true,
    },
    showDiningOptionToggleInMenu: {
        displayName: 'Show dining option toggle in menu',
        type: 'bool',
        required: true,
        tooltip: {
            text: 'The toggle will only be shown if there are exactly 2 options.',
        },
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
    },
    dineInFulfillmentIcon: {
        displayName: 'Dine In Fulfillment Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    toGoFulfillmentIcon: {
        displayName: 'To Go Fulfillment Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    collapseModGroupIcon: {
        displayName: 'Collapse Mod Group Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    expandModGroupIcon: {
        displayName: 'Expand Mod Group Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    campusCardIcon: {
        displayName: 'Campus Card Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    cashIcon: {
        displayName: 'Cash Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    compCardIcon: {
        displayName: 'Comp Card Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    couponIcon: {
        displayName: 'Coupon Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    creditCardIcon: {
        displayName: 'Credit Card Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    donationIcon: {
        displayName: 'Donation Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    ebtIcon: {
        displayName: 'EBT Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    giftCardIcon: {
        displayName: 'Gift Card Icon',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 100,
            height: 100,
        },
    },
    scaleImage: {
        displayName: 'Scale Image',
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        size: {
            width: 464,
            height: 300,
        },
    },
    showRewardImages: {
        displayName: 'Show Reward Images',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'Only Punchh and Olo rewards will show images!',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    showDescriptionForRewards: {
        displayName: 'Display Reward Descriptions',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'Only works for Olo + loyalties.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    showRewardTerms: {
        displayName: 'Show Reward Details Button',
        type: 'bool',
        required: true,
        defaultValue: false,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'Show a "Tap for details" button on rewards that have terms. The button opens a dialog with the reward description and terms.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    hideRewardPoints: {
        displayName: 'Hide Reward Points',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'Hide the point cost (e.g. "(500 points)") from reward names on the loyalty screen.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    hideRewardValue: {
        displayName: 'Hide Reward Value',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'Hide the value (e.g. "($5.00)" or "(10%)") from reward names on the loyalty screen.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    showInlineLoyaltyAtCheckout: {
        displayName: 'Show Inline Rewards at Checkout',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'Show rewards inline on the payment options screen instead of requiring navigation to the full loyalty screen.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    applyRewardOnTap: {
        displayName: 'Apply Reward on Tap',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'Apply a reward as soon as the guest taps it on the loyalty screen instead of requiring a separate Apply tap. With single-reward redemption, tapping another reward replaces the applied one. Does not affect the loyalty payment screen.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    reducedHeightSize: {
        displayName: 'Reduced Height Size',
        type: 'stringEnum',
        enum: KioskReducedHeightSize,
        enumNameHelper: KioskReducedHeightSizeHelper.name,
        required: true,
        ui: 'dropdown',
        tabId: 'kiosk-settings-v2',
        writeRight: UserRight.ManageChannelSettings,
        exclusiveTo: 'vitrine',
    },
    fullScreenValidation: {
        displayName: 'Full Screen Validation',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, the validation flow (when a guest presses the checkout button in the cart) will be shown in full screen instead of a popup.',
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    promptDiningOptionsAtCheckout: {
        displayName: 'Prompt Dining Options at Checkout',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, the dining options will be prompted at checkout. Be careful, this can mean certain items will be unavailable for guests depending on how the menu is configured.',
            isWarning: true,
        },
        writeRight: UserRight.ManageChannelSettings,
    },
    skipDiningOptionIfCartOnlyContainsCategories: {
        type: 'array',
        elementType: 'int',
        elementValues: allEnumValues(MenuItemCategory),
        displayName: 'Skip Prompting Dining Option If Cart Only Contains Categories',
        exclusiveTo: 'vitrine',
        tabId: 'kiosk-settings-v2',
        ui: 'table',
        placeholder: 'Select Item Categories',
        required: true,
        minCount: 1,
        tooltip: {
            text: 'If the order contains ONLY items in the selected categories, the table tracker will not be dispensed.',
        },
        isNullable: true,
    },
    checkmarkSelectedMods: {
        displayName: 'Checkmark Selected Mods',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, any mod that is selected will show a checkmark.',
        },
    },
    collapseNestedModGroups: {
        displayName: 'Collapse Nested Mod Groups Into Popup',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'When enabled, nested modifier groups collapse behind the mod tile. Tapping opens a popup step view.',
        },
    },
    alwaysPromptGuestName: {
        displayName: 'Always Prompt Guest Name at Checkout',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, we will surface the guest name pre-filled if the guest name is ever auto-populated on the order. Does not apply when every item was scanned and the Guest Name identifier has a Scanned-Only Order Autofill Value.',
        },
    },
    setOrderGuestNameFromGuest: {
        displayName: 'Set Order Guest Name From Guest',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
    },
    tableTrackerSuccessImage: {
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        displayName: 'Order Success Table tracker Image',
        size: {
            width: 952,
            height: 476,
        },
        tooltip: {
            text: 'Image to show when the order is successfully completed and the table tracker is requested.',
        },
        tabId: 'kiosk-settings-v2',
    },
    tableTrackerFailureImage: {
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        displayName: 'Table tracker Failure Image',
        size: {
            width: 952,
            height: 476,
        },
        tooltip: {
            text: 'Image to show when we fail to communicate with the table-tracker.',
        },
        tabId: 'kiosk-settings-v2',
    },
    setOrderGuestPhoneNumberFromLoyalty: {
        displayName: 'Set Order Guest Phone Number From Loyalty',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, and the location has a guest phone number identifier, we will set the guest phone number from the loyalty system.',
        },
    },
    menuItemViewDetailsStyle: {
        displayName: 'Menu Item View Details Style',
        type: 'stringEnum',
        enum: MenuItemViewDetailsStyle,
        enumNameHelper: MenuItemViewDetailsStyleHelper.name,
        required: true,
        tabId: 'kiosk-settings-v2',
        ui: 'dropdown',
        exclusiveTo: 'vitrine',
    },
    showItemMacroNutritionSummary: {
        displayName: 'Show Item Macro Nutrition Summary',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        writeRight: UserRight.ManageChannelSettings,
        tooltip: {
            text: 'Show the running calorie, fat, carbohydrate, and protein summary on menu item views.',
        },
    },
    useLanguageToggle: {
        displayName: 'Use Language Toggle',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, and the menu has exactly two languages, we will show a toggle rather than a dropdown for the language selection.',
        },
    },
    guestSurveyOptions: {
        displayName: 'Guest Survey Options',
        type: 'array',
        elementType: 'object',
        required: true,
        tabId: 'kiosk-settings-v2',
        ui: 'accordion',
        uiSubtype: 'GuestSurveyOptions',
        maxCount: 10,
        fields: {
            label: {
                type: 'keyString',
                displayName: 'Label',
                required: true,
                tooltip: {
                    text: 'This label will be used to voice the survey option when using Voice Navigation.',
                },
            },
            image: {
                type: 'array',
                elementType: 'object',
                elementSubtype: 'Image',
                elementUniqueIdFieldName: 'origUrl',
                maxCount: 1,
                required: true,
                size: {
                    width: 128,
                    height: 128,
                },
            },
        },
    },
    unmaskScannerLogsUntil: {
        displayName: 'Unmask Scanner Logs Until',
        type: 'timestamp',
        required: true,
        tabId: 'kiosk-settings-v2',
        tooltip: {
            text: 'The scanner data logs by default are masked. This setting allows you to unmask them until a specific date.',
        },
        showDatePickerOnly: true,
        pastDateLimit: 'disallowed',
        futureDateLimit: { value: 7, unit: 'days' },
        isNullable: true,
        writeRight: UserRight.BiteOps,
    },
    maintenanceCoverImage: {
        type: 'array',
        elementType: 'object',
        elementSubtype: 'Image',
        elementUniqueIdFieldName: 'origUrl',
        maxCount: 1,
        displayName: 'Maintenance Cover Image',
        size: {
            width: 1080,
            height: 1920,
        },
        tooltip: {
            text: 'Image to show when the kiosk is in maintenance mode.',
        },
        tabId: 'kiosk-settings-v2',
    },
    maskSingularLoyaltyAuthMethod: {
        displayName: 'Mask Singular Loyalty Auth Method',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, and there is only one loyalty auth method available, the input field will be masked.',
        },
    },
    resetItemButtonEnabled: {
        displayName: 'Reset Item Button',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, a reset button is shown on item views to clear all modifications.',
        },
    },
    showMenuItemInfoButton: {
        displayName: 'Show Menu Item Info Button',
        type: 'bool',
        required: true,
        tabId: 'kiosk-settings-v2',
        exclusiveTo: 'vitrine',
        tooltip: {
            text: 'If enabled, an info button is shown on menu item cards and item views.',
        },
    },
};
//# sourceMappingURL=settings_kiosk_schema.js.map